/**
 * Project hub → Datos → Dimensiones (Plan J, J2).
 *
 * A dimension is declared once and serves two things: the filters a
 * report offers, and the rules an access scope is written from. This
 * screen is where an analyst declares them and — more often — where they
 * find out why one is not working.
 *
 * THREE RULES THIS FILE IS BUILT AROUND
 * -------------------------------------
 * 1. **A backend identifier never reaches the screen, and a missing
 *    label is loud.** `status`, `origin` and each `usable_as` member go
 *    through `resolveStatus` against a dictionary here; a value with no
 *    entry renders as the raw key with a warning glyph and a tooltip
 *    saying the label is missing — never as a prettified guess.
 *    `__tests__/dimensions-pane.test.ts` asserts every member of the
 *    closed unions has an entry AND renders `StatusCell` / `OriginCell`
 *    / `UsableAsCell` to read what an unlisted value puts on screen.
 *    The dictionary assertion alone was not a guard: with the cells
 *    inline, swapping `resolveStatus` for a title-cased prettification
 *    left all 19 tests and `tsc` green.
 * 2. **Colour is never the only channel.** Every state is a
 *    `StatusChip`, which carries a glyph and a word; the product owner
 *    has red-green colour blindness and the `ok` / `schema_drift` pair
 *    has to be separable with the colour removed.
 * 3. **Nothing is counted until it is counted.** The list endpoint
 *    carries no distinct count — values come from
 *    `GET …/dimensions/{id}/values`, which reads the live project file.
 *    So the Valores cell offers to fetch and shows an em-dash until it
 *    has, rather than rendering a `0` that means "not asked".
 *
 * WHY THIS PANE ALSO READS THE LOADER
 * -----------------------------------
 * An unmapped value does not become a dimension STATUS — it fails the
 * whole reload (`dimensions/enrich._census_unmapped`), the previous data
 * stays live, and the refusal lands in `loader_jobs.error_summary`. On
 * this screen the dimension would then read `needs_reload`, which is
 * true and reads as "just reload it" — while a reload will fail again
 * for the same reason. The callout above the table is what closes that
 * gap, and reading the last job is the only place the reason exists.
 *
 * That callout could not render at all until 2026-09-07: the summary
 * parser was written against a hand-typed example instead of a string
 * the backend produces. See `ENRICH_SUMMARY_RE` for the whole story and
 * for the two-ended contract that now holds it.
 */

import { Button } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  EmptyState,
  Section,
  StatusChip,
  resolveStatus,
  type Column,
  type RowAction,
  type StatusDef,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { resolveLocalized } from "@pervasivemind/dashboard-render";
import { Layers, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import { projectsApi, type ProjectLoaderResponse } from "@/lib/builder-api";
import {
  DIMENSION_ORIGINS,
  dimensionsApi,
  type DimensionOut,
  type DimensionValuesOut,
} from "@/lib/dimensions-api";
import { DimensionFormModal } from "@/pages/projects/DimensionFormModal";
import type { DisplayLabel, Project } from "@/types";

type TFn = (key: string, params?: Record<string, string | number>) => string;

// ── Dictionaries ─────────────────────────────────────────────────────

/**
 * `schemas/dimensions.DimensionStatus`, each with a glyph-bearing tone.
 *
 * On the tones, because two of them are a judgement and not an obvious
 * mapping:
 *
 * * `schema_drift` is `warning` and not `danger` even though one of its
 *   two meanings ("the data has no such column") does fail a build. The
 *   other meaning is a benign disagreement between the live file and the
 *   record, and one status covers both — so the WORD carries the state
 *   and the DETAIL says which of the two it is. Painting the benign case
 *   red would teach the analyst to ignore red.
 * * `unresolvable` is the only `danger`: it means nothing about the row
 *   can be determined at all, which is the one state where the screen
 *   cannot say what a scope on it would do.
 */
export function statusDict(t: TFn): Record<string, StatusDef> {
  return {
    ok: { label: t("dimensions.status.ok"), tone: "success" },
    needs_reload: {
      label: t("dimensions.status.needs_reload"),
      tone: "info",
    },
    inert: { label: t("dimensions.status.inert"), tone: "warning" },
    schema_drift: {
      label: t("dimensions.status.schema_drift"),
      tone: "warning",
    },
    unresolvable: {
      label: t("dimensions.status.unresolvable"),
      tone: "danger",
    },
  };
}

/** `ck_dimensions_origin` — the whole vocabulary, no fourth value. */
export function originDict(t: TFn): Record<string, StatusDef> {
  return {
    column: { label: t("dimensions.origin.column"), tone: "neutral" },
    mapping: { label: t("dimensions.origin.mapping"), tone: "info" },
    computed: { label: t("dimensions.origin.computed"), tone: "info" },
  };
}

export function usableAsDict(t: TFn): Record<string, StatusDef> {
  return {
    filter: { label: t("dimensions.usableAs.filter"), tone: "neutral" },
    scope: { label: t("dimensions.usableAs.scope"), tone: "info" },
  };
}

// ── The reload refusal, parsed ───────────────────────────────────────

/**
 * `dimensions/enrich.ENRICH_ERROR_KINDS`, mirrored.
 *
 * A closed vocabulary on both sides is what lets this screen recognise
 * an enrichment refusal in a loader job WITHOUT pattern-matching on
 * prose.
 *
 * This mirror used to be described as safe to drift, because a kind the
 * backend adds and this set does not simply raises no callout. That is
 * the wrong direction to be relaxed about, and the slice proved it: "no
 * callout" IS the failure this file just spent a fix on, and it looks
 * from the outside exactly like a reload that keeps failing for no
 * stated reason. So the drift is pinned — the builder's
 * `tests/test_enrich_summary_contract.py` reads this literal and fails
 * if the backend can raise a kind that is not here.
 */
export const ENRICH_ERROR_KINDS: ReadonlySet<string> = new Set([
  "missing_output_column",
  "mapping_config_incomplete",
  "expression_empty",
  "output_column_collision",
  "duplicate_output_column",
  "join_key_not_in_raw",
  "join_key_unscopable_type",
  "mapping_key_type_mismatch",
  "unmapped_values",
  "expression_rejected",
  "expression_not_single_column",
  "expression_not_row_local",
  "expression_references_computed",
  "expression_invalid",
  "row_count_changed",
]);

export interface EnrichFailure {
  kind: string;
  /** `null` for the refusals that are not about one dimension. */
  dimensionKey: string | null;
  /** The backend's own prose, verbatim. */
  detail: string;
}

/**
 * `loader_jobs.error_summary`, in the shape the loader actually writes.
 *
 * THE PREFIX IS THE WHOLE STORY. This regex shipped anchored on `^\[`,
 * written against a hand-typed `"[unmapped_values] dimension 'region':
 * …"`. No such row has ever existed: `service_loader.py` composes every
 * failed job's summary as `f"{type(exc).__name__}: {str(exc) or '<no
 * msg>'}"`, so the real string starts `"DimensionEnrichmentError: "`.
 * The regex matched NOTHING the backend can produce —
 * `parseEnrichFailure` returned `null` for every enrichment refusal
 * that will ever exist, so the callout below never rendered, and the
 * only on-screen explanation of the most likely operational event this
 * slice introduces did not exist. The dimension read `Falta recargar`
 * ("just reload it"), the analyst reloaded, it failed again for the
 * same reason, forever, and the reason lived only in the loader row.
 *
 * The class prefix is OPTIONAL rather than required, and that is not a
 * fallback: the closed `ENRICH_ERROR_KINDS` check below is what
 * decides, and it is what keeps a reconciliation or connector failure
 * (whose kinds are disjoint sets — `scopes/build.BUILD_ERROR_KINDS`,
 * `scopes/predicate.PREDICATE_ERROR_KINDS`) from claiming this callout.
 * Requiring the prefix would buy nothing and would re-arm exactly this
 * bug: the day a producer writes the message without it, the callout
 * would go quiet again with every test still green.
 *
 * The contract now has both ends nailed down. The real strings live in
 * `__tests__/loader-summary.fixture.json`; the builder's
 * `tests/test_enrich_summary_contract.py` rebuilds them by RAISING the
 * real `DimensionEnrichmentError` and evaluating the real
 * `service_loader.py` expression, so the next change to the message
 * shape fails a test instead of silently killing the callout again.
 */
const ENRICH_SUMMARY_RE =
  /^(?:[A-Za-z_][A-Za-z0-9_]*:\s*)?\[([a-z_]+)\]\s*(?:dimension '([^']*)':\s*)?([\s\S]*)$/;

/**
 * `"DimensionEnrichmentError: [unmapped_values] dimension 'region': …"`
 * → the parts.
 *
 * `null` for anything that is not an enrichment refusal — every other
 * loader failure already has a home in the Auto-refresh tab and does not
 * belong on this screen.
 */
export function parseEnrichFailure(
  summary: string | null | undefined,
): EnrichFailure | null {
  if (typeof summary !== "string" || summary === "") return null;
  const match = ENRICH_SUMMARY_RE.exec(summary.trim());
  if (match === null) return null;
  const kind = match[1] ?? "";
  if (!ENRICH_ERROR_KINDS.has(kind)) return null;
  // Group 2 is OPTIONAL, and TypeScript types every index of a
  // `RegExpExecArray` as `string` — at runtime a group that did not
  // participate is `undefined`. The `??` is what turns that into the
  // honest `null` ("this refusal names no dimension") instead of the
  // string "undefined" reaching the callout title.
  return {
    kind,
    dimensionKey: match[2] ?? null,
    detail: (match[3] ?? "").trim(),
  };
}

/**
 * The loader response → the failure the callout renders, or `null`.
 *
 * This is the BODY of the effect below, lifted out as a named function
 * on purpose: this app's vitest runs in the `node` environment and
 * components are rendered with `react-dom/server`, which never runs a
 * `useEffect`. Left inline, the step between "the API answered" and
 * "the callout has something to show" was the one link in the chain no
 * test could reach — and it is the link that was broken for the whole
 * slice.
 *
 * Only the MOST RECENT job counts, and only while it is `failed`: a
 * refusal that a later successful reload has already superseded is not
 * something to put in front of an analyst as current.
 */
export function reloadFailureFromLoader(
  out: Pick<ProjectLoaderResponse, "recent_jobs">,
): EnrichFailure | null {
  const last = out.recent_jobs[0];
  if (last === undefined || last.status !== "failed") return null;
  return parseEnrichFailure(last.error_summary);
}

/**
 * The callout above the table: why the last reload refused, and what to
 * do about it.
 *
 * A component rather than inline JSX so a test can render it without a
 * DOM, an API or an effect — see `reloadFailureFromLoader`. The
 * `unmapped_values` body is separate copy because it is the one refusal
 * whose fix belongs to the CLIENT (a row missing from a Master sheet),
 * not to the analyst reading this screen.
 */
export function ReloadFailureCallout({
  failure,
  t,
}: {
  failure: EnrichFailure;
  t: TFn;
}): JSX.Element {
  return (
    <Callout
      tone="danger"
      title={
        failure.dimensionKey === null
          ? t("dimensions.reloadFailed.titleGeneric")
          : t("dimensions.reloadFailed.title", {
              dimension: failure.dimensionKey,
            })
      }
      moreLabel={t("dimensions.reloadFailed.more")}
      lessLabel={t("dimensions.reloadFailed.less")}
      more={
        <span className="whitespace-pre-wrap font-mono text-[13px]">
          {failure.detail}
        </span>
      }
    >
      {failure.kind === "unmapped_values"
        ? t("dimensions.reloadFailed.unmapped")
        : t("dimensions.reloadFailed.body")}
    </Callout>
  );
}

// ── Cells ────────────────────────────────────────────────────────────

/** What the Definición cell says, per origin. Pure, so the test can
 *  pin that a config missing its fields renders as absent rather than
 *  as `undefined`. */
export function definitionText(row: DimensionOut, t: TFn): string {
  const get = (name: string): string => {
    const value = row.config[name];
    return typeof value === "string" ? value : "";
  };
  if (row.origin === "column") return get("source_column");
  if (row.origin === "mapping") {
    return t("dimensions.def.mapping", {
      collection: get("master_collection_slug"),
      sheet: get("master_sheet_name"),
      key: get("key_column"),
      value: get("value_column"),
      match: get("match_column"),
    });
  }
  if (row.origin === "computed") return get("sql_expression");
  return "";
}

/**
 * The three chip cells, each a component and none of them inline.
 *
 * WHY THEY ARE NOT INLINE IN `columns`
 * ------------------------------------
 * Rule 1 at the top of this file is that a backend identifier never
 * reaches the screen prettified, and `resolveStatus` is the only thing
 * that enforces it. While these cells were inline arrow functions
 * inside the component, `__tests__/dimensions-pane.test.ts` could
 * assert everything about `statusDict` and NOTHING about the screen:
 * swapping `resolveStatus` for `dict[raw] ?? titleCase(raw)` — the
 * exact 2026-09 audit defect, which put `Column Not In Raw` in front of
 * an analyst with a neutral dash glyph, a plausible label for a state
 * nobody designed — left all 19 tests green and `tsc` at 0. Measured,
 * not assumed.
 *
 * Exported, they are renderable with `react-dom/server`, so the test
 * reads the MARKUP of an unknown value and sees the raw key, the
 * `font-mono` treatment and the warning glyph. That is the guard;
 * the dictionary test alone was not one.
 */
export function OriginCell({
  row,
  origins,
  t,
}: {
  row: DimensionOut;
  origins: Record<string, StatusDef>;
  t: TFn;
}): JSX.Element {
  const st = resolveStatus(row.origin, origins, t("common.missingLabel"));
  return (
    <StatusChip
      label={st.label}
      tone={st.tone}
      unknown={st.unknown}
      title={st.title}
    />
  );
}

export function StatusCell({
  row,
  statuses,
  t,
}: {
  row: DimensionOut;
  statuses: Record<string, StatusDef>;
  t: TFn;
}): JSX.Element {
  const st = resolveStatus(row.status, statuses, t("common.missingLabel"));
  return (
    <StatusChip
      label={st.label}
      tone={st.tone}
      unknown={st.unknown}
      // The backend's own sentence is the better tooltip whenever there
      // IS a label; `st.title` is set only for an unknown key, and then
      // it must win — "this label is missing" outranks a detail that
      // describes a state the screen just failed to name.
      title={st.title ?? row.status_detail}
    />
  );
}

export function UsableAsCell({
  row,
  usable,
  t,
}: {
  row: DimensionOut;
  usable: Record<string, StatusDef>;
  t: TFn;
}): JSX.Element {
  if (row.usable_as.length === 0) {
    return (
      <StatusChip
        label={t("dimensions.usableAs.none")}
        tone="warning"
        title={t("dimensions.usableAs.noneHint")}
      />
    );
  }
  return (
    <>
      {row.usable_as.map((member) => {
        const st = resolveStatus(member, usable, t("common.missingLabel"));
        return (
          <StatusChip
            key={member}
            label={st.label}
            tone={st.tone}
            unknown={st.unknown}
            title={st.title}
          />
        );
      })}
    </>
  );
}

/**
 * The Valores cell.
 *
 * `null` means NOT ASKED, and it renders as an em-dash plus the action —
 * never as `0`. A zero here would be an invented count: the list
 * endpoint does not carry one, and the only honest number comes from
 * reading the live file.
 */
export function valuesCellText(
  loaded: DimensionValuesOut | null,
  t: TFn,
): string {
  if (loaded === null) return "—";
  return t("dimensions.values.n", { n: loaded.total });
}

// ── The pane ─────────────────────────────────────────────────────────

interface ValuesState {
  loading: boolean;
  data: DimensionValuesOut | null;
  error: string | null;
}

const VALUES_PAGE = 50;

export function ProjectDimensionsPane({
  project,
}: {
  project: Project;
}): JSX.Element {
  const { t, locale } = useT();

  const [rows, setRows] = useState<DimensionOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DimensionOut | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, ValuesState>>({});
  const [reloadFailure, setReloadFailure] = useState<EnrichFailure | null>(
    null,
  );
  const [loaderNote, setLoaderNote] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const out = await dimensionsApi.list(project.slug);
      setRows(out.dimensions);
      // A fresh list invalidates every value page: the endpoint reads
      // the live file, and the reason to re-list is usually that the
      // file moved.
      setValues({});
      setExpandedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [project.slug]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The last reload, only to recognise an enrichment refusal. A failure
  // to READ it is stated rather than swallowed — but it is a secondary
  // diagnostic, so it goes in the table's footnote and does not stop the
  // screen. `GET …/loader` is gated by `operations` while this pane is
  // gated by `edit_projects`, so a 403 here is an ordinary outcome for a
  // real user and must not read as a broken screen.
  useEffect(() => {
    let cancelled = false;
    projectsApi.loader
      .get(project.slug)
      .then((out) => {
        if (cancelled) return;
        setLoaderNote(null);
        setReloadFailure(reloadFailureFromLoader(out));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReloadFailure(null);
        setLoaderNote(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [project.slug]);

  const statuses = useMemo(() => statusDict(t), [t]);
  const origins = useMemo(() => originDict(t), [t]);
  const usable = useMemo(() => usableAsDict(t), [t]);
  // No `?? id` here: `DIMENSION_ORIGINS` and `originDict` cover the same
  // closed vocabulary, and the test asserts it. A fallback would be a
  // second, silent answer to "what is this origin called" — exactly the
  // shape `resolveStatus` refuses for statuses.
  const originOptions = useMemo(
    () =>
      DIMENSION_ORIGINS.map((id) => ({ value: id, label: origins[id].label })),
    [origins],
  );

  const loadValues = useCallback(
    async (row: DimensionOut): Promise<void> => {
      setValues((current) => ({
        ...current,
        [row.id]: { loading: true, data: null, error: null },
      }));
      try {
        const out = await dimensionsApi.values(project.slug, row.id, {
          limit: VALUES_PAGE,
        });
        setValues((current) => ({
          ...current,
          [row.id]: { loading: false, data: out, error: null },
        }));
      } catch (err) {
        setValues((current) => ({
          ...current,
          [row.id]: {
            loading: false,
            data: null,
            error: err instanceof Error ? err.message : String(err),
          },
        }));
      }
    },
    [project.slug],
  );

  const toggleRow = useCallback(
    (row: DimensionOut): void => {
      setExpandedId((current) => (current === row.id ? null : row.id));
      if (values[row.id] === undefined && row.column_type !== null) {
        void loadValues(row);
      }
    },
    [values, loadValues],
  );

  const onDelete = async (row: DimensionOut): Promise<void> => {
    const name = resolveLocalized(row.label as DisplayLabel, locale) || row.key;
    if (!window.confirm(t("dimensions.delete.confirm", { name }))) return;
    setError(null);
    try {
      await dimensionsApi.remove(project.slug, row.id);
      await refresh();
    } catch (err) {
      // 409 when a scope currently granting access to this project rules
      // on it. The message names the scopes; there is no force flag, on
      // purpose — deleting would revoke, from behind `edit_projects`,
      // access only a `manage_users` holder could have granted.
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const columns: Column<DimensionOut>[] = [
    {
      id: "dimension",
      header: t("dimensions.col.dimension"),
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-neutral-900">
            {resolveLocalized(row.label as DisplayLabel, locale) || row.key}
          </div>
          <div className="truncate font-mono text-[13px] text-neutral-500">
            {row.key}
          </div>
        </div>
      ),
    },
    {
      id: "origin",
      header: t("dimensions.col.origin"),
      width: "w-32",
      cell: (row) => <OriginCell row={row} origins={origins} t={t} />,
    },
    {
      id: "definition",
      header: t("dimensions.col.definition"),
      cell: (row) => {
        const text = definitionText(row, t);
        if (text === "") {
          return (
            <span
              className="text-neutral-500"
              title={t("dimensions.def.noneHint")}
            >
              —
            </span>
          );
        }
        return (
          <span
            className={
              row.origin === "computed"
                ? "block max-w-md truncate font-mono text-[13px] text-neutral-600"
                : "block max-w-md truncate text-neutral-600"
            }
            title={text}
          >
            {text}
          </span>
        );
      },
    },
    {
      id: "values",
      header: t("dimensions.col.values"),
      width: "w-40",
      cell: (row) => {
        const state = values[row.id];
        if (state?.loading === true) {
          return (
            <span className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500">
              <Spinner size={12} ariaLabel={t("common.loading")} />
              {t("common.loading")}
            </span>
          );
        }
        if (row.column_type === null) {
          return (
            <span
              className="text-[13px] text-neutral-500"
              title={t("dimensions.values.unavailableHint")}
            >
              {t("dimensions.values.unavailable")}
            </span>
          );
        }
        const loaded = state?.data ?? null;
        if (loaded !== null) {
          return (
            <span className="text-neutral-900">
              {valuesCellText(loaded, t)}
            </span>
          );
        }
        return (
          <button
            type="button"
            className="rounded-md px-1 text-[13px] font-medium text-neutral-700 underline underline-offset-2 hover:bg-neutral-100"
            onClick={(event) => {
              event.stopPropagation();
              setExpandedId(row.id);
              void loadValues(row);
            }}
          >
            {t("dimensions.values.view")}
          </button>
        );
      },
    },
    {
      id: "usedIn",
      header: t("dimensions.col.usedIn"),
      width: "w-56",
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-1">
          {row.used_by_scopes.length === 0 ? (
            <span
              className="text-neutral-500"
              title={t("dimensions.usedIn.noneHint")}
            >
              —
            </span>
          ) : (
            <span
              className="text-[13px] text-neutral-700"
              title={row.used_by_scopes.join(", ")}
            >
              {t("dimensions.usedIn.scopes", { n: row.used_by_scopes.length })}
            </span>
          )}
          <UsableAsCell row={row} usable={usable} t={t} />
        </div>
      ),
    },
    {
      id: "status",
      header: t("dimensions.col.status"),
      width: "w-48",
      cell: (row) => <StatusCell row={row} statuses={statuses} t={t} />,
    },
  ];

  const actions: RowAction<DimensionOut>[] = [
    {
      id: "edit",
      label: t("dimensions.actions.edit"),
      icon: <Pencil size={14} />,
      onSelect: (row) => {
        setEditing(row);
        setModalOpen(true);
      },
    },
    {
      id: "delete",
      label: t("dimensions.actions.delete"),
      icon: <Trash2 size={14} />,
      danger: true,
      onSelect: (row) => void onDelete(row),
    },
  ];

  const openCreate = (): void => {
    setEditing(null);
    setModalOpen(true);
  };

  return (
    <div className="space-y-4">
      {reloadFailure !== null && (
        <ReloadFailureCallout failure={reloadFailure} t={t} />
      )}

      {error !== null && (
        <Callout tone="danger" title={t("dimensions.error.title")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}

      <Section
        title={t("dimensions.title")}
        description={t("dimensions.subtitle")}
        flush
        actions={
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={openCreate}
          >
            {t("dimensions.add")}
          </Button>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-5 py-10 text-sm text-neutral-500">
            <Spinner size={16} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            actions={actions}
            caption={t("dimensions.title")}
            onRowClick={toggleRow}
            footnote={
              loaderNote === null
                ? undefined
                : t("dimensions.loaderUnavailable", { message: loaderNote })
            }
            expandedRow={(row) =>
              expandedId === row.id ? (
                <DimensionDetail
                  row={row}
                  state={values[row.id]}
                  t={t}
                />
              ) : null
            }
            empty={
              <EmptyState
                icon={<Layers size={24} />}
                title={t("dimensions.empty.title")}
                description={t("dimensions.empty.desc")}
                action={
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Plus size={14} />}
                    onClick={openCreate}
                  >
                    {t("dimensions.add")}
                  </Button>
                }
              />
            }
          />
        )}
      </Section>

      <DimensionFormModal
        open={modalOpen}
        project={project}
        dimension={editing}
        originOptions={originOptions}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          void refresh();
        }}
      />
    </div>
  );
}

/**
 * The expanded row: the backend's own explanation of the state, and the
 * values when they have been read.
 *
 * The detail prose is rendered verbatim on purpose — it is the sentence
 * that names the column, the type and what a scope on this dimension
 * would do, and paraphrasing it in the dictionary would put a second
 * definition of the state in a place that cannot see the file.
 */
function DimensionDetail({
  row,
  state,
  t,
}: {
  row: DimensionOut;
  state: ValuesState | undefined;
  t: TFn;
}): JSX.Element {
  const data = state?.data ?? null;
  return (
    <div className="space-y-3">
      <div>
        <p className="text-[13px] font-semibold text-neutral-700">
          {t("dimensions.detail.whyTitle")}
        </p>
        <p className="mt-0.5 text-[13px] text-neutral-600">
          {row.status_detail}
        </p>
      </div>

      {row.in_stored_schema === false && (
        <p className="text-[13px] text-neutral-600">
          {t("dimensions.detail.notInStoredSchema")}
        </p>
      )}

      {state?.error != null && (
        <Callout tone="danger" title={t("dimensions.values.errorTitle")}>
          <span className="whitespace-pre-wrap">{state.error}</span>
        </Callout>
      )}

      {state?.loading === true && (
        <span className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500">
          <Spinner size={12} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </span>
      )}

      {data !== null && (
        <div className="space-y-2">
          <p className="text-[13px] text-neutral-600">
            {t("dimensions.values.summary", {
              shown: data.values.length,
              total: data.total,
              column: data.column,
              type: data.column_type,
            })}
          </p>
          {!data.scopable && (
            <Callout tone="warning" title={t("dimensions.values.notScopableTitle")}>
              {t("dimensions.values.notScopable", { type: data.column_type })}
            </Callout>
          )}
          <p className="text-[13px] text-neutral-600">
            {t("dimensions.values.nulls", { n: data.null_count })}
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {data.values.map((entry) => (
              <li
                key={String(entry.value)}
                className="rounded-md bg-neutral-100 px-2 py-0.5 font-mono text-[13px] text-neutral-700"
                title={t("dimensions.values.rowCount", { n: entry.row_count })}
              >
                {entry.value === null
                  ? t("dimensions.values.nullValue")
                  : String(entry.value)}
              </li>
            ))}
          </ul>
          <p className="text-[13px] text-neutral-500">
            {t("dimensions.values.versionKey", { key: data.version_key })}
          </p>
        </div>
      )}
    </div>
  );
}
